import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { UserService } from "@main/users";
import { SelectItem } from "primeng/api";
import { MultiUserFormDef } from "../dto/multi-user-form.dto";
import { MultiUserFormAssociation, NewMultiUserFormAssociation } from "../dto/multi-user-form-associations.dto";
import { MultiUserFormService } from "../services/multi-user-form.service";
import { NewMultiUserFormAssociationLink } from "../dto/multi-user-form-association-links.dto";
import { MUFUserTargets, MultiUserFormStage, StageTargetType } from "../dto/multi-user-form-stage.dto";
import { Dialog } from "primeng/dialog";
import { UserHelperService } from "@main/users/user-helper.service";
import CryptoES from "crypto-es";
import { SelfRegistrationFormService } from "@project/self-registration/forms/self-registration-form.service";
import { ValidationPatterns } from "@bbwt/modules/validation";
import { OrganizationService } from "@main/companies";

@Component({
    selector: "multi-user-form-create-instance-dialog",
    templateUrl: "./multi-user-form-create-instance.component.html"
})

export class MultiUserFormCreateInstanceComponent implements OnInit {
    @Output() readonly onCancel = new EventEmitter();
    @Output() readonly onCreate = new EventEmitter<NewMultiUserFormAssociation>();

    @Input() episodeId: string = null;
    @Input() patientId: string = null;
    @Input() multiUserFormDef: MultiUserFormDef = null;
    @Input() isMandatoryForm = false;
    @Input() walkInId: string = null;

    @Input() anonymousSelfRegForm: boolean = false;
    @Input() selfRegOrgUrl: string;
    @Input() selfRegPatientId: string;

    showWarning = false;

    @ViewChild("mufDialog") mufDialog: Dialog

    visible = true;
    stages: SelectItem[] = [];
    stagesUsers: any[] = [];
    userTypesDD: any[] = [];
    stagesValue: any = {};
    multiUserFormAssociation: MultiUserFormAssociation;
    newFormName: string = "";
    password: string = "";
    passwordHint: string = "";
    hasExternalUser = false;

    constructor(
        private userService: UserService,
        private organizationService : OrganizationService,
        private multiUserFormService: MultiUserFormService,
        private selfRegistrationFormService: SelfRegistrationFormService,
        private userHelperService: UserHelperService) {
    }

    async ngOnInit(): Promise<void> {
        if (this.multiUserFormDef) {
            this.newFormName = this.multiUserFormDef.name;
            await this.processFormStages(this.multiUserFormDef)
        }
    }

    get validationPatterns() {
        return ValidationPatterns;
    }

    async processFormStages(data: MultiUserFormDef) {
        const formStages = data.multiUserFormStages.sort(x => x.id_original);
        try {

            let currentOrgId: number;
            let instanceTargets: MUFUserTargets[]
            if (this.anonymousSelfRegForm) {
                const selfRegInfo = await this.organizationService.getBySelfRegistrationUrl(this.selfRegOrgUrl);
                currentOrgId = selfRegInfo.id_original;
                instanceTargets = await this.selfRegistrationFormService.getInstanceTargetsByUrl(this.selfRegOrgUrl);
            } else {
                currentOrgId = parseInt(this.userHelperService.organizationId);
                instanceTargets = await this.multiUserFormService.getInstanceTargets(data.id);
            }

            instanceTargets.forEach(x => {
                if (x.organizationId.includes(currentOrgId)) {
                    if (this.userTypesDD[x.userTypeId]) {
                        this.userTypesDD[x.userTypeId].push(<SelectItem>{label: x.name, value: x.id});
                    } else {
                        this.userTypesDD[x.userTypeId] = [<SelectItem>{label: x.name, value: x.id}];
                    }
                }
            });

            formStages.map(stage => {
                stage.userTypeIds.forEach(id => {
                    if (this.userTypesDD[id]) {
                        if (this.stagesUsers[stage.id_original]) {
                            this.stagesUsers[stage.id_original].push(...this.userTypesDD[id]);
                        } else {
                            this.stagesUsers[stage.id_original] = [...this.userTypesDD[id]];
                        }
                    }
                });
            });
            this.hasExternalUser = formStages.some(x => x.stageTargetType == StageTargetType.ExternalUsers);
            this.password = this.hasExternalUser ? "" : "-";
            this.passwordHint = this.hasExternalUser ? "" : "-";
            this.stages = formStages.map<SelectItem>(x => {
                return {label: `${x.name} (${x.innerTabKey})`, value: x};
            });
        } catch (error) {
            this.cancelMUFInstantiation();
        }
    }

    cancelMUFInstantiation() {
        if (this.isMandatoryForm) {
            this.showWarning = true;
            return;
        }
        this.stages = [];
        this.stagesValue = {};
        this.userTypesDD = [];
        this.multiUserFormDef = null;
        this.multiUserFormAssociation = null;
        this.onCancel.emit();
    }

    async instantiateMUF() {
        if (this.hasExternalUser && (!this.password || !this.passwordHint)) {
            // If password or description are empty, interrupt
            return;
        }

        this.visible = false;

        const links: NewMultiUserFormAssociationLink[] = [];
        this.stages.forEach(x => {
            const stage = <MultiUserFormStage>(x.value);

            links.push({
                externalUserEmail: stage.stageTargetType == 1 ? this.stagesValue[stage.innerTabKey] : "",
                userId: this.getUserIdByStageType(stage),
                stageTargetType: stage.stageTargetType,
                stageId: stage.id_original
            })
        });

        const instance = <NewMultiUserFormAssociation>{
            multiUserFormDefinitionId: this.multiUserFormDef.id_original,
            description: this.newFormName,
            multiUserFormAssociationLinks: links,
            episodeId: this.episodeId,
            patientId: this.anonymousSelfRegForm ? this.selfRegPatientId : this.patientId,
            walkInId: this.walkInId,
            created: new Date(),
            password: CryptoES.SHA512(this.password).toString(),
            passwordHint: this.passwordHint
        }

        this.episodeId = null;
        this.stages = [];
        this.stagesValue = {};
        this.userTypesDD = [];
        this.multiUserFormDef = null;

        this.onCreate.emit(instance);
    }

    private getUserIdByStageType(stage: MultiUserFormStage): string {
        switch (stage.stageTargetType) {
            case StageTargetType.Self:
                return this.anonymousSelfRegForm ? this.selfRegPatientId : this.userService.currentUser.id;
            case StageTargetType.InnerGroups:
                return this.stagesValue[stage.innerTabKey]
            default:
                return "";
        }
    }
}
